import Hue from '@assets/hue_hero.svg';
import GalleryTabButton from '@atoms/GalleryTabButton/GalleryTabButton';
import Image from '@atoms/Image/Image';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IProductImageGallery } from '@models/IProductImageGallery';
import Carousel from '@molecules/Carousel/Carousel';
import EmbeddedVideo from '@molecules/EmbeddedVideo/EmbeddedVideo';
import PaginationArrows from '@molecules/PaginationArrows/PaginationArrows';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import WebGlModelViewer from '@molecules/WebGlModelViewer/WebGlModelViewer';
import updateDataLayer from '@utils/helpers/gtm';
import replaceWithNonBreakingHyphens from '@utils/helpers/replaceWithNonBreakingHyphens';
import scrollIntoViewWithOffset from '@utils/helpers/scrollIntoViewWithOffset';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

const enum GalleryTabsEnum {
  Video = 'video',
  Images = 'images',
  ThreeD = 'view_3d',
}

interface Props extends IProductImageGallery {
  readonly imageClassNames?: string;
  readonly threeDClassNames?: string;
  readonly isMainHero?: boolean;
}

const ProductImageGallery = ({
  anchorId = '',
  backgroundImage,
  dividerText = '/',
  imageIcon,
  imageLabel = '',
  images,
  imageClassNames = 'h-[350px] md:h-[500px]',
  isMainHero = true,
  meta,
  subtitle,
  threeDClassNames = 'h-[500px] md:h-[700px]',
  threeDIcon,
  threeDLabel,
  threeDModel,
  title,
  video,
  videoIcon,
  videoLabel = '',
}: Props) => {
  const isSmallDesktop = useMediaQuery(1440);
  const isTablet = useMediaQuery(1024);
  const isMobile = useMediaQuery(768);
  const [paginationRef, { width: paginationWidth }] = useResizeObserver();
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState(
    threeDModel?.src
      ? GalleryTabsEnum.ThreeD
      : images?.length
        ? GalleryTabsEnum.Images
        : GalleryTabsEnum.Video
  );
  const [currentImageSlideIndex, setCurrentImageSlideIndex] = useState(1);
  const [is3DModelLoading, setIs3DModelLoading] = useState(true);
  const carouselNavId = 'product-image-gallery';
  const imagesPaginationCounterText = `${currentImageSlideIndex} ${dividerText} ${
    images?.length ?? 0
  }`;
  const tabs = [!!video, !!images?.length, !!threeDModel].filter(Boolean);
  const showTabs = tabs.length >= 2;
  const hasTitleAndSubtitle = title && subtitle;

  const setTab = (tab: GalleryTabsEnum) => {
    setActiveTab(tab);
    updateDataLayer({
      event: 'selectContent',
      content_type: 'product_image_side_menu',
      selection: tab,
    });
  };

  const TitleTag = isMainHero ? 'h1' : 'h2';
  const getSubtitleStyle = () => {
    if (isMainHero) return isTablet ? 'h5' : 'h3';
    return isSmallDesktop ? 'h6' : 'h3';
  };

  if (!video && !images?.length) return null;

  return (
    <SoftLoadWrapper isAnimationDisabled={!isMainHero}>
      <section
        id={`a-${anchorId}`}
        className={classNames(
          'w-full bg-black-100 text-white',
          isMainHero ? 'pt-20 pb-20 xl:pt-28' : 'lg:relative'
        )}
        data-epi-block-id={meta?.contentLink}
        data-testid="CT-14-Product-Image-Gallery"
      >
        <div
          className={classNames({
            flex: hasTitleAndSubtitle,
            'container flex-col xl:flex-row': isMainHero,
            'flex-col-reverse max-lg:gap-y-7.5 lg:flex-row-reverse lg:items-center lg:justify-center lg:gap-x-20':
              !isMainHero,
          })}
          ref={containerRef}
        >
          {hasTitleAndSubtitle && (
            <div
              className={classNames(
                'flex flex-col justify-center xl:w-2/5 xl:gap-10 xl:text-left',
                isMainHero
                  ? 'gap-6 text-center max-xl:items-center max-xl:px-2.5 max-xl:pb-10 xl:pr-15 2xl:pr-24'
                  : 'w-full gap-y-4 text-left max-lg:container lg:w-[45%] lg:shrink-0 lg:pr-[90px] xl:pr-[140px]'
              )}
            >
              <TitleTag
                className={classNames({
                  mount: isMainHero || !isSmallDesktop,
                })}
                data-testid="CT-14-Product-Image-Gallery-Title"
              >
                {replaceWithNonBreakingHyphens(title)}
              </TitleTag>

              <p
                className={classNames('text-grey-400', getSubtitleStyle())}
                data-testid="CT-14-Product-Image-Gallery-Subtitle"
              >
                {subtitle}
              </p>
            </div>
          )}

          <div
            className={classNames('relative z-[1] flex w-full flex-col', {
              'xl:w-3/5': hasTitleAndSubtitle,
              'min-w-0': !isMainHero,
            })}
          >
            {activeTab === GalleryTabsEnum.Video && (
              <motion.div
                className={classNames(
                  'w-full pr-0',
                  isMainHero ? 'xl:pr-[150px]' : 'flex justify-center'
                )}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                exit={{ opacity: 0 }}
              >
                <EmbeddedVideo
                  {...video}
                  embedded
                  sectionClassName={classNames({
                    'md:max-w-fit': !isMainHero,
                  })}
                  contentClassName={classNames({
                    'md:flex': !isMainHero,
                  })}
                  videoContainerClassName={classNames({
                    'md:h-[380px] lg:h-[260px] xl:h-[340px] 2xl:h-[380px]':
                      !isMainHero,
                  })}
                />
              </motion.div>
            )}

            {activeTab === GalleryTabsEnum.ThreeD && (
              <motion.div
                className={classNames('w-full pr-0', {
                  'xl:pr-[150px]': isMainHero && hasTitleAndSubtitle,
                  'flex justify-center': !isMainHero,
                })}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                exit={{ opacity: 0 }}
              >
                {is3DModelLoading && (
                  <LoadingSpinner
                    extraClasses={classNames(
                      'pointer-events-none absolute top-0 left-0 flex h-full w-full items-center',
                      {
                        'xl:w-4/5': hasTitleAndSubtitle && isMainHero,
                      }
                    )}
                  />
                )}

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: is3DModelLoading ? 0 : 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <WebGlModelViewer
                    modelUrl={threeDModel?.src}
                    onModelLoading={(isLoading: boolean) =>
                      setIs3DModelLoading(isLoading)
                    }
                    className={classNames({
                      [threeDClassNames]: !hasTitleAndSubtitle || !isMainHero,
                    })}
                  />
                </motion.div>

                <motion.div
                  className="-z-[1] pointer-events-none absolute top-0 left-0 flex h-full w-full items-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: is3DModelLoading ? 0 : 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <img
                    alt="background-hue"
                    src={`${Hue}`}
                    className={classNames('w-full object-contain', {
                      'xl:w-4/5': hasTitleAndSubtitle && isMainHero,
                      'h-[130%]': !hasTitleAndSubtitle || !isMainHero,
                    })}
                  />
                </motion.div>
              </motion.div>
            )}

            {activeTab === GalleryTabsEnum.Images && (
              <motion.div
                className="w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                exit={{ opacity: 0 }}
              >
                <div className="relative select-none">
                  <Carousel
                    tabIndex={0}
                    slidesPerView={1}
                    loop
                    navId={carouselNavId}
                    onSlideChange={(swiper) => {
                      setCurrentImageSlideIndex(swiper.realIndex + 1);
                    }}
                    id={carouselNavId}
                    data-testid="CT-14-Product-Image-Gallery-Carousel"
                  >
                    {images?.map((image, index) => (
                      <SwiperSlide
                        key={`Product-Gallery-${image.desktop?.url}`}
                        className="stretch-card flex items-center justify-center"
                      >
                        <div
                          className={classNames(
                            'flex w-full select-none',
                            imageClassNames
                          )}
                        >
                          <Image
                            image={image}
                            classes="flex justify-center w-full"
                            imageClasses="object-contain w-full h-full"
                            lazyLoad={index !== 0}
                            dataTestId={`CT-14-Product-Image-Gallery-Image-${index}`}
                          />
                        </div>
                      </SwiperSlide>
                    ))}

                    {backgroundImage && (
                      <Image
                        image={backgroundImage}
                        classes="w-full"
                        imageClasses="absolute left-1/2 -translate-x-1/2 xl:flex xl:px-[200px] h-full w-auto inset-y-0 z-0"
                        dataTestId="CT-14-Product-Image-Gallery-Background-Image"
                      />
                    )}
                  </Carousel>

                  <div className="-z-[1] pointer-events-none absolute top-0 left-0 flex h-full w-full items-center justify-center">
                    <img
                      alt="background-hue"
                      src={`${Hue}`}
                      className="w-full object-contain lg:w-3/4"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        <AnimatePresence>
          {showTabs && (
            <motion.div
              className={classNames({
                'max-lg:container lg:absolute lg:w-full': !isMainHero,
              })}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              animate={{
                opacity: 1,
              }}
            >
              <ShadowContainerHorizontal
                className={classNames({
                  'mt-5': isMainHero && activeTab === GalleryTabsEnum.ThreeD,
                  'mt-10 lg:mt-28':
                    isMainHero && activeTab !== GalleryTabsEnum.ThreeD,
                  'mt-7.5': !isMainHero,
                  'md:mt-10':
                    !isMainHero && activeTab === GalleryTabsEnum.Images,
                })}
                shadowColour="from-black-100"
                innerWrapperClassName={tabs.length === 3 ? 'max-md:flex' : ''}
              >
                <div
                  className={classNames(
                    'flex items-center gap-x-5',
                    isMainHero
                      ? 'justify-center'
                      : 'justify-start lg:mb-px lg:justify-center'
                  )}
                  data-testid="CT-14-Product-Image-Gallery-Tab-Buttons"
                >
                  {video && (
                    <div className="pointer-events-auto">
                      <GalleryTabButton
                        onClick={() => {
                          setCurrentImageSlideIndex(1);
                          setTab(GalleryTabsEnum.Video);
                          if (isMobile) {
                            scrollIntoViewWithOffset(
                              containerRef.current as HTMLElement,
                              120
                            );
                          }
                        }}
                        label={videoLabel}
                        icon={videoIcon}
                        isActive={activeTab === GalleryTabsEnum.Video}
                      />
                    </div>
                  )}
                  {threeDModel && (
                    <div className="pointer-events-auto">
                      <GalleryTabButton
                        onClick={() => {
                          setTab(GalleryTabsEnum.ThreeD);
                        }}
                        isActive={activeTab === GalleryTabsEnum.ThreeD}
                        label={threeDLabel}
                        icon={threeDIcon}
                      />
                    </div>
                  )}

                  {images && (
                    <div className="pointer-events-auto relative">
                      <GalleryTabButton
                        onClick={() => {
                          setTab(GalleryTabsEnum.Images);
                          if (isMobile) {
                            scrollIntoViewWithOffset(
                              containerRef.current as HTMLElement,
                              120
                            );
                          }
                        }}
                        label={imageLabel}
                        icon={imageIcon}
                        isActive={activeTab === GalleryTabsEnum.Images}
                        rightDesktopPadding={
                          images?.length > 1 &&
                          activeTab === GalleryTabsEnum.Images
                            ? paginationWidth + 45
                            : 15
                        }
                      />

                      {images?.length > 1 &&
                        activeTab === GalleryTabsEnum.Images && (
                          <div
                            ref={paginationRef}
                            className="absolute top-0 right-[15px] flex h-full items-center max-lg:hidden"
                          >
                            <PaginationArrows
                              carouselNavId={carouselNavId}
                              componentName="Product Image Gallery"
                              counterText={imagesPaginationCounterText}
                              isCounterTextBetweenArrows
                              productName={title}
                              hasWrapper={false}
                            />
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </ShadowContainerHorizontal>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </SoftLoadWrapper>
  );
};

export default ProductImageGallery;
